import React from "react";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { useDotButton } from "@/components/ui/carousel-dot-button";
import { cls, cn } from "@/lib/utils";
import type { ProjectName } from "@/app/projects/types";
import { useProjectGallery } from "../../providers/ProjectGalleryProvider";
import { Skeleton } from "../skeleton";
import GalleryItem from "./project-gallery-item";

const NUMBER_LOADING_SECTIONS = 3;

interface ProjectGalleryProps {
  projectName: ProjectName;
  onSetApi?: ((api: CarouselApi) => void) | null;
}

function ProjectGallery({ projectName, onSetApi = null }: ProjectGalleryProps) {
  const [galleryIsLoading, setGalleryIsLoading] = React.useState(true);
  const [api, setApi] = React.useState<CarouselApi>();
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(api);
  const { projects, isLoading } = useProjectGallery();

  const showProjects = !isLoading && projects !== null;

  const handleOnSetApi = React.useCallback(
    (value: CarouselApi) => {
      if (onSetApi) {
        onSetApi(value);
      }

      setApi(value);
    },
    [onSetApi],
  );

  const handleOnLoad = React.useCallback(() => {
    setGalleryIsLoading(false);
  }, []);

  return (
    <>
      <Carousel
        setApi={handleOnSetApi}
        opts={{ loop: true }}
        className={cls`w-full`}
      >
        <CarouselContent>
          {showProjects &&
            projects[projectName].map(url => (
              <GalleryItem key={url} url={url} onLoad={handleOnLoad} />
            ))}

          {galleryIsLoading &&
            Array.from({ length: NUMBER_LOADING_SECTIONS }).map((_, index) => (
              <CarouselItem
                key={`${index + 2}`}
                className={cls`pl-5 basis-full md:basis-4/5 aspect-4/3`}
              >
                <Skeleton className="w-full h-full z-100 bg-primary" />
              </CarouselItem>
            ))}
        </CarouselContent>
      </Carousel>

      {showProjects && (
        <div
          className={cls`
          flex flex-row
          justify-center items-center gap-2
          w-full h-fit
        `}
        >
          {scrollSnaps.map((_, index) => (
            <Button
              aria-label={`Go to slide ${(index + 1).toString()}`}
              key={`dot-${(index + 1).toString()}`}
              onClick={() => {
                onDotButtonClick(index);
              }}
              className={cn(
                `rounded-full bg-transparent w-3 h-3 p-0`,
                index !== selectedIndex && `bg-primary hover:bg-primary/50`,
              )}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default React.memo(ProjectGallery);
